import { useState, useMemo, useEffect, useRef } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetAllWarrantyClaimsQuery, useGetWarrantyClaimsStatusCountsQuery, useGetLookUpDropdownListQuery } from '../../../../../api';
import { useGetDealerDropdownListQuery, useGetDealerCodeDropdownListQuery } from '../../../../../api/endpoints/dealer';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../../utils/filterUtils';

// Bu hook SADECE liste sayfasının (index.jsx + WarrantyClaimsTable) ihtiyaç duyduğu
// state/veriyi barındırır: filtreler, sıralama, sayfalama, dropdown seçenekleri, tab sayaçları
// ve claim listesinin kendisi. Kaydetme/aksiyon (onayla/reddet/iade/sil) mantığı
// `useWarrantyClaimsMutations` içinde - detay sayfası artık bu hook'u import etmiyor,
// dolayısıyla detay sayfasında bu dropdown/liste sorguları gereksiz yere tetiklenmiyor.

// Initial filter state - tek bir yerde tanımlanıyor
const INITIAL_FILTER_STATE = {
    dealerId: null,
    warrClaimsNo: null,
    warrClaimsNoMatchMode: null,
    chassisNo: null,
    chassisNoMatchMode: null,
    plateNo: null,
    plateNoMatchMode: null,
    customerName: null,
    customerNameMatchMode: null,
    preApprovalNo: null,
    preApprovalNoMatchMode: null,
    CustomerStatementId: null,
    CustomerStatementIdMatchMode: null,
    bosBranchCode: null,
    bosBranchCodeMatchMode: null,
    country: null,
    countryMatchMode: null,
    warrType: null,
    warrTypeMatchMode: null,
    totalPrice: null,
    totalPriceMatchMode: null,
    waitingApprovalInfo: null,
    waitingApprovalInfoMatchMode: null,
    sbNumber: null,
    sbNumberMatchMode: null,
    createdName: null,
    createdNameMatchMode: null,
    createdDate: null,
    createdDateMatchMode: null,
    claimDate: null,
    claimDateMatchMode: null,
    sendApprovalDate: null,
    sendApprovalDateMatchMode: null,
    repairDate: null,
    repairDateMatchMode: null,
    warrantyStartDate: null,
    warrantyStartDateMatchMode: null,
    standardWarrantyEndDate: null,
    standardWarrantyEndDateMatchMode: null,
    extendedWarrantyEndDate: null,
    extendedWarrantyEndDateMatchMode: null,
    statusId: null,
    modifiedName: null,
    modifiedNameMatchMode: null,
    modifiedDate: null,
    modifiedDateMatchMode: null
};

// localStorage'dan filtre ve sıralama bilgilerini oku
const loadFiltersFromStorage = (tabIndex) => {
    try {
        const storageKey = `warrantyClaimsFilters_${tabIndex}`;
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
            const parsed = JSON.parse(savedData);
            // Date string'lerini Date objesine çevir
            const dateFields = ['createdDate', 'claimDate', 'sendApprovalDate', 'repairDate', 'warrantyStartDate', 'standardWarrantyEndDate', 'extendedWarrantyEndDate', 'modifiedDate'];
            dateFields.forEach((field) => {
                if (parsed.filters[field] && typeof parsed.filters[field] === 'string') {
                    parsed.filters[field] = new Date(parsed.filters[field]);
                }
            });
            return parsed;
        }
    } catch (error) {
        console.error('Error reading filters from localStorage:', error);
    }
    return null;
};

// localStorage'a filtre ve sıralama bilgilerini kaydet
const saveFiltersToStorage = (tabIndex, filters, sortField, sortOrder, lazyParams, dealerFilterValue, warrantyTypeFilterValue) => {
    try {
        const storageKey = `warrantyClaimsFilters_${tabIndex}`;
        const dataToSave = {
            filters,
            sortField,
            sortOrder,
            lazyParams,
            dealerFilterValue: dealerFilterValue || '',
            warrantyTypeFilterValue: warrantyTypeFilterValue || ''
        };
        localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    } catch (error) {
        console.error('Error saving filters to localStorage:', error);
    }
};

const useWarrantyClaimsList = () => {
    const { getCurrentLanguageId } = useLanguage();

    // localStorage'dan aktif tab index'ini oku, yoksa 0 kullan
    const isFirstRender = useRef(true);

    const [activeIndex, setActiveIndex] = useState(() => {
        try {
            const savedIndex = localStorage.getItem('warrantyClaimsActiveTab');
            return savedIndex ? parseInt(savedIndex, 10) : 0;
        } catch (error) {
            console.error('Error reading activeIndex from localStorage:', error);
            return 0;
        }
    });

    // İlk render'da localStorage'dan filtre ve sıralama bilgilerini oku
    const initialStoredData = loadFiltersFromStorage(activeIndex);

    const [dealerSearchText, setDealerSearchText] = useState('');
    const [debouncedDealerSearch, setDebouncedDealerSearch] = useState('');
    const [dealerFilterValue, setDealerFilterValue] = useState(() => {
        return initialStoredData?.dealerFilterValue || '';
    });
    const [dealerCodeSearchText, setDealerCodeSearchText] = useState('');
    const [debouncedDealerCodeSearch, setDebouncedDealerCodeSearch] = useState('');
    const [dealerCodeFilterValue, setDealerCodeFilterValue] = useState('');
    const [warrantyTypeFilterValue, setWarrantyTypeFilterValue] = useState(() => {
        return initialStoredData?.warrantyTypeFilterValue || '';
    });

    const [lazyParams, setLazyParams] = useState(() => {
        if (initialStoredData?.lazyParams) {
            return initialStoredData.lazyParams;
        }
        return {
            first: 0,
            rows: 10,
            page: 1
        };
    });

    const [sortField, setSortField] = useState(() => {
        return initialStoredData?.sortField || null;
    });

    const [sortOrder, setSortOrder] = useState(() => {
        return initialStoredData?.sortOrder || 0;
    });

    // Filter state - localStorage'dan oku
    const [activeFilters, setActiveFilters] = useState(() => {
        if (initialStoredData?.filters) {
            return initialStoredData.filters;
        }
        return INITIAL_FILTER_STATE;
    });

    const languageId = useMemo(() => getCurrentLanguageId(), [getCurrentLanguageId]);

    // Memoize queryParams to prevent unnecessary API calls
    // Remove undefined values to ensure stable object reference
    const queryParams = useMemo(() => {
        const params = {
            status: activeIndex,
            bosBranchCode: '',
            bosBranchSiteCode: '',
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            languageId: languageId
        };

        if (activeFilters.dealerId && !Array.isArray(activeFilters.dealerId)) {
            params.dealerId = activeFilters.dealerId;
        }
        const filters = buildDynamicFilters(activeFilters, activeIndex);
        if (filters && filters.length > 0) {
            params.filters = filters;
        }
        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts && sorts.length > 0) {
            params.sorts = sorts;
        }
        return params;
    }, [activeIndex, activeFilters, lazyParams.page, lazyParams.rows, languageId, sortField, sortOrder]);

    const countParams = useMemo(() => {
        const params = {
            languageId: languageId
        };
        if (activeFilters.dealerId && !Array.isArray(activeFilters.dealerId)) {
            params.dealerId = activeFilters.dealerId;
        }
        const filters = buildDynamicFilters(activeFilters, activeIndex);
        if (filters && filters.length > 0) {
            params.filters = filters;
        }
        return params;
    }, [activeFilters, activeIndex, languageId]);

    const { data: tabCounts = {} } = useGetWarrantyClaimsStatusCountsQuery(countParams);

    const { data: apiWarrantyClaims, isLoading, isFetching, error, refetch } = useGetAllWarrantyClaimsQuery(queryParams);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedDealerSearch(dealerSearchText);
        }, 500);

        return () => clearTimeout(timer);
    }, [dealerSearchText]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedDealerCodeSearch(dealerCodeSearchText);
        }, 500);

        return () => clearTimeout(timer);
    }, [dealerCodeSearchText]);

    // Dealer dropdown için API çağrısı
    const { data: dealerList = [], isFetching: isFetchingDealers } = useGetDealerDropdownListQuery({
        RowCount: 50,
        SearchText: debouncedDealerSearch
    });

    const dealerOptionsCache = useRef(new Map());

    useEffect(() => {
        dealerList.forEach((dealer) => {
            dealerOptionsCache.current.set(dealer.dealerId, dealer.name);
        });
    }, [dealerList]);

    const dealerOptions = useMemo(() => {
        const apiOptions = dealerList.map((dealer) => ({
            label: dealer.name,
            value: dealer.dealerId
        }));
        const existingValues = new Set(apiOptions.map((opt) => opt.value));
        if (Array.isArray(activeFilters.dealerId)) {
            activeFilters.dealerId.forEach((id) => {
                if (!existingValues.has(id)) {
                    const cachedLabel = dealerOptionsCache.current.get(id);
                    if (cachedLabel) {
                        apiOptions.unshift({ label: cachedLabel, value: id });
                    }
                }
            });
        }
        return apiOptions;
    }, [dealerList, activeFilters.dealerId]);

    // Dealer code dropdown için API çağrısı
    const { data: dealerCodeList = [] } = useGetDealerCodeDropdownListQuery({
        RowCount: 100,
        SearchText: debouncedDealerCodeSearch
    });

    const dealerCodeOptionsCache = useRef(new Map());

    useEffect(() => {
        if (Array.isArray(dealerCodeList)) {
            dealerCodeList.forEach((d) => {
                dealerCodeOptionsCache.current.set(String(d.erpDealerId), String(d.sapDealerId));
            });
        }
    }, [dealerCodeList]);

    const dealerCodeOptions = useMemo(() => {
        if (!Array.isArray(dealerCodeList)) return [];
        const apiOptions = dealerCodeList.map((d) => ({
            label: String(d.sapDealerId),
            value: String(d.erpDealerId)
        }));
        const existingValues = new Set(apiOptions.map((opt) => opt.value));
        if (Array.isArray(activeFilters.bosBranchCode)) {
            activeFilters.bosBranchCode.forEach((code) => {
                if (!existingValues.has(code)) {
                    const cachedLabel = dealerCodeOptionsCache.current.get(code);
                    if (cachedLabel) {
                        apiOptions.unshift({ label: cachedLabel, value: code });
                    }
                }
            });
        }
        return apiOptions;
    }, [dealerCodeList, activeFilters.bosBranchCode]);

    // Warranty Type dropdown için API çağrısı
    const { data: warrantyTypeList = [] } = useGetLookUpDropdownListQuery({
        RowCount: 100,
        LanguageId: languageId
    });

    // Transform warranty type list for dropdown options
    const warrantyTypeOptions = useMemo(() => {
        return warrantyTypeList.map((type) => ({
            label: type.translatedText,
            value: type.code,
            code: type.code
        }));
    }, [warrantyTypeList]);

    const { data: countryLookupList = [] } = useGetLookUpDropdownListQuery({
        LanguageId: languageId,
        GroupCode: 'COUNTRIES'
    });

    const countryOptions = useMemo(() => {
        if (!Array.isArray(countryLookupList)) return [];
        return countryLookupList.map((item) => ({
            label: item.translatedText,
            value: item.code
        }));
    }, [countryLookupList]);

    const handleFilterChange = (updatedFilters) => {
        const apiFilters = {
            dealerId: Array.isArray(updatedFilters.dealerId) && updatedFilters.dealerId.length > 0 ? updatedFilters.dealerId : null,
            warrClaimsNo: updatedFilters.warrClaimsNo || null,
            warrClaimsNoMatchMode: updatedFilters.warrClaimsNoMatchMode || null,
            chassisNo: updatedFilters.chassisNo || null,
            chassisNoMatchMode: updatedFilters.chassisNoMatchMode || null,
            plateNo: updatedFilters.plateNo || null,
            plateNoMatchMode: updatedFilters.plateNoMatchMode || null,
            customerName: updatedFilters.customerName || null,
            customerNameMatchMode: updatedFilters.customerNameMatchMode || null,
            preApprovalNo: updatedFilters.preApprovalNo || null,
            preApprovalNoMatchMode: updatedFilters.preApprovalNoMatchMode || null,
            CustomerStatementId: updatedFilters.CustomerStatementId || null,
            CustomerStatementIdMatchMode: updatedFilters.CustomerStatementIdMatchMode || null,
            bosBranchCode: Array.isArray(updatedFilters.bosBranchCode) && updatedFilters.bosBranchCode.length > 0 ? updatedFilters.bosBranchCode : null,
            bosBranchCodeMatchMode: FilterMatchMode.CONTAINS,
            country: Array.isArray(updatedFilters.country) && updatedFilters.country.length > 0 ? updatedFilters.country : null,
            countryMatchMode: FilterMatchMode.EQUALS,
            warrType: updatedFilters.warrType || null,
            warrTypeMatchMode: FilterMatchMode.EQUALS, // Warranty type için her zaman EQUALS
            totalPrice: updatedFilters.totalPrice || null,
            totalPriceMatchMode: updatedFilters.totalPriceMatchMode || null,
            waitingApprovalInfo: updatedFilters.waitingApprovalInfo || null,
            waitingApprovalInfoMatchMode: updatedFilters.waitingApprovalInfoMatchMode || null,
            sbNumber: updatedFilters.sbNumber || null,
            sbNumberMatchMode: updatedFilters.sbNumberMatchMode || null,
            createdName: updatedFilters.createdName || null,
            createdNameMatchMode: updatedFilters.createdNameMatchMode || null,
            createdDate: updatedFilters.createdDate || null,
            createdDateMatchMode: updatedFilters.createdDateMatchMode || null,
            claimDate: updatedFilters.claimDate || null,
            claimDateMatchMode: updatedFilters.claimDateMatchMode || null,
            sendApprovalDate: updatedFilters.sendApprovalDate || null,
            sendApprovalDateMatchMode: updatedFilters.sendApprovalDateMatchMode || null,
            repairDate: updatedFilters.repairDate || null,
            repairDateMatchMode: updatedFilters.repairDateMatchMode || null,
            warrantyStartDate: updatedFilters.warrantyStartDate || null,
            warrantyStartDateMatchMode: updatedFilters.warrantyStartDateMatchMode || null,
            standardWarrantyEndDate: updatedFilters.standardWarrantyEndDate || null,
            standardWarrantyEndDateMatchMode: updatedFilters.standardWarrantyEndDateMatchMode || null,
            extendedWarrantyEndDate: updatedFilters.extendedWarrantyEndDate || null,
            extendedWarrantyEndDateMatchMode: updatedFilters.extendedWarrantyEndDateMatchMode || null,
            statusId: Array.isArray(updatedFilters.statusId) && updatedFilters.statusId.length > 0 ? updatedFilters.statusId : null,
            modifiedName: updatedFilters.modifiedName || null,
            modifiedNameMatchMode: updatedFilters.modifiedNameMatchMode || null,
            modifiedDate: updatedFilters.modifiedDate || null,
            modifiedDateMatchMode: updatedFilters.modifiedDateMatchMode || null
        };

        setActiveFilters(apiFilters);

        const newLazyParams = {
            first: 0,
            rows: lazyParams.rows,
            page: 1
        };

        setLazyParams(newLazyParams);

        // localStorage'a kaydet
        saveFiltersToStorage(activeIndex, apiFilters, sortField, sortOrder, newLazyParams, dealerFilterValue, warrantyTypeFilterValue);
    };

    const initFilters = () => {
        setActiveFilters(INITIAL_FILTER_STATE);

        setDealerSearchText('');
        setDebouncedDealerSearch('');
        setDealerFilterValue('');
        setWarrantyTypeFilterValue('');

        const newLazyParams = {
            first: 0,
            rows: 10,
            page: 1
        };

        setLazyParams(newLazyParams);

        // localStorage'dan ilgili tab'ın filtresini temizle
        saveFiltersToStorage(activeIndex, INITIAL_FILTER_STATE, null, 0, newLazyParams, '', '');
    };

    const onPage = (event) => {
        const newLazyParams = {
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        };
        setLazyParams(newLazyParams);

        // localStorage'a kaydet
        saveFiltersToStorage(activeIndex, activeFilters, sortField, sortOrder, newLazyParams, dealerFilterValue, warrantyTypeFilterValue);
    };

    const handleSortChange = (event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;
        const backendColumn = getBackendColumnName(nextSortField);

        if (!backendColumn && nextSortOrder !== 0) {
            return;
        }

        setSortField(nextSortField);
        setSortOrder(nextSortOrder);

        const newLazyParams = {
            first: 0,
            rows: lazyParams.rows,
            page: 1
        };

        setLazyParams(newLazyParams);

        // localStorage'a kaydet
        saveFiltersToStorage(activeIndex, activeFilters, nextSortField, nextSortOrder, newLazyParams, dealerFilterValue, warrantyTypeFilterValue);
    };

    // Tab değiştiğinde localStorage'dan o tab'ın filtrelerini yükle
    useEffect(() => {
        // İlk render'da çalışma
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        // Yeni tab'ın localStorage'dan kaydedilmiş filtrelerini oku
        const storedData = loadFiltersFromStorage(activeIndex);

        if (storedData) {
            // Eğer kaydedilmiş filtre varsa, onu kullan
            setActiveFilters(storedData.filters || INITIAL_FILTER_STATE);
            setSortField(storedData.sortField || null);
            setSortOrder(storedData.sortOrder || 0);
            setLazyParams(
                storedData.lazyParams || {
                    first: 0,
                    rows: 10,
                    page: 1
                }
            );
            setDealerFilterValue(storedData.dealerFilterValue || '');
            setWarrantyTypeFilterValue(storedData.warrantyTypeFilterValue || '');
        } else {
            // Kaydedilmiş filtre yoksa, initial state'i kullan
            setActiveFilters(INITIAL_FILTER_STATE);
            setSortField(null);
            setSortOrder(0);
            setLazyParams({
                first: 0,
                rows: 10,
                page: 1
            });
            setDealerFilterValue('');
            setWarrantyTypeFilterValue('');
        }

        // Dealer search state'lerini sıfırla
        setDealerSearchText('');
        setDebouncedDealerSearch('');

        if (activeIndex !== undefined) {
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex]);

    // Tab değişikliklerini localStorage'da sakla
    const handleSetActiveIndex = (index) => {
        try {
            localStorage.setItem('warrantyClaimsActiveTab', index.toString());
            setActiveIndex(index);
        } catch (error) {
            setActiveIndex(index);
        }
    };

    return {
        apiWarrantyClaims,
        isLoading,
        isFetching,
        error,
        activeIndex,
        lazyParams,
        sortField,
        sortOrder,
        activeFilters,
        queryParams,
        handleFilterChange,
        initFilters,
        onPage,
        onSort: handleSortChange,
        refetch,
        setActiveIndex: handleSetActiveIndex,
        dealerOptions,
        dealerSearchText,
        setDealerSearchText,
        dealerFilterValue,
        setDealerFilterValue,
        isFetchingDealers,
        dealerCodeOptions,
        dealerCodeSearchText,
        setDealerCodeSearchText,
        dealerCodeFilterValue,
        setDealerCodeFilterValue,
        warrantyTypeOptions,
        warrantyTypeFilterValue,
        setWarrantyTypeFilterValue,
        countryOptions,
        tabCounts
    };
};

export default useWarrantyClaimsList;

const backendColumnMap = {
    dealerId: 'DealerId',
    warrClaimsNo: 'WarrClaimsNo',
    chassisNo: 'ChassisNo',
    preApprovalNo: 'PreAppNo',
    workOrder: 'CustomerStatementId',
    CustomerStatementId: 'CustomerStatementId',
    createdDate: 'CreatedDate',
    createdName: 'CreatedName',
    totalPrice: 'TotalPrice',
    customerName: 'CustomerName',
    plateNo: 'PlateNo',
    warrType: 'WarrType',
    brand: 'Brand',
    bosBranchName: 'BosBranchName',
    bosBranchCode: 'BosBranchCode',
    country: 'Country',
    sbNumber: 'SbNumber',
    claimDate: 'claimDate',
    sendApprovalDate: 'SendApprovalDate',
    repairDate: 'RepairDate',
    warrantyStartDate: 'WarrantyStartDate',
    standardWarrantyEndDate: 'StandardWarrantyEndDate',
    extendedWarrantyEndDate: 'ExtendedWarrantyEndDate',
    waitingApprovalInfo: 'WaitingApprovalInfo',
    statusId: 'Status',
    modifiedName: 'ModifiedName',
    modifiedDate: 'ModifiedDate'
};

const getBackendColumnName = (field) => {
    if (!field) return null;
    return backendColumnMap[field] || null;
};

const getOperatorValue = (matchMode) => {
    return matchModeToOperator[matchMode] || matchModeToOperator[FilterMatchMode.CONTAINS];
};

const buildDynamicFilters = (filtersState, activeIndex) => {
    const filters = [];
    const dateFields = ['createdDate', 'claimDate', 'sendApprovalDate', 'repairDate', 'warrantyStartDate', 'standardWarrantyEndDate', 'extendedWarrantyEndDate', 'modifiedDate'];

    const pushFilter = (field, value, matchMode) => {
        if (value === null || value === undefined || value === '') return;

        if (Array.isArray(value)) {
            const column = getBackendColumnName(field);
            if (!column) return;
            value
                .map((item) => String(item ?? '').trim())
                .filter((item) => item !== '')
                .forEach((singleValue) => {
                    filters.push({
                        column,
                        operator: getOperatorValue(matchMode || FilterMatchMode.EQUALS),
                        value: singleValue
                    });
                });
            return;
        }

        const column = getBackendColumnName(field);
        if (!column) return;

        // Tarih alanları için Date objesini ISO 8601 formatına çevir (BE'nin beklediği format)
        let filterValue = value;
        if (dateFields.includes(field) && value instanceof Date) {
            // ISO 8601 formatı: "2026-01-26T00:00:00.000Z"
            filterValue = value.toISOString();
        } else {
            // Text değerleri için trim işlemi uygula (null kontrolü ile)
            filterValue = String(value);
            if (filterValue && typeof filterValue === 'string') {
                filterValue = filterValue.trim();
            }
            // Trim sonrası boş string kontrolü
            if (filterValue === '') return;
        }

        filters.push({
            column,
            operator: getOperatorValue(matchMode),
            value: filterValue
        });
    };

    pushFilter('dealerId', filtersState?.dealerId, FilterMatchMode.EQUALS);
    pushFilter('warrClaimsNo', filtersState?.warrClaimsNo, filtersState?.warrClaimsNoMatchMode);
    pushFilter('chassisNo', filtersState?.chassisNo, filtersState?.chassisNoMatchMode);
    pushFilter('plateNo', filtersState?.plateNo, filtersState?.plateNoMatchMode);
    pushFilter('customerName', filtersState?.customerName, filtersState?.customerNameMatchMode);
    pushFilter('preApprovalNo', filtersState?.preApprovalNo, filtersState?.preApprovalNoMatchMode);
    pushFilter('CustomerStatementId', filtersState?.CustomerStatementId, filtersState?.CustomerStatementIdMatchMode);
    pushFilter('bosBranchCode', filtersState?.bosBranchCode, filtersState?.bosBranchCodeMatchMode);
    pushFilter('country', filtersState?.country, filtersState?.countryMatchMode);
    pushFilter('warrType', filtersState?.warrType, filtersState?.warrTypeMatchMode);
    pushFilter('totalPrice', filtersState?.totalPrice, filtersState?.totalPriceMatchMode);
    pushFilter('sbNumber', filtersState?.sbNumber, filtersState?.sbNumberMatchMode);
    pushFilter('createdName', filtersState?.createdName, filtersState?.createdNameMatchMode);
    pushFilter('createdDate', filtersState?.createdDate, filtersState?.createdDateMatchMode);
    pushFilter('claimDate', filtersState?.claimDate, filtersState?.claimDateMatchMode);
    pushFilter('sendApprovalDate', filtersState?.sendApprovalDate, filtersState?.sendApprovalDateMatchMode);
    pushFilter('repairDate', filtersState?.repairDate, filtersState?.repairDateMatchMode);
    pushFilter('warrantyStartDate', filtersState?.warrantyStartDate, filtersState?.warrantyStartDateMatchMode);
    pushFilter('standardWarrantyEndDate', filtersState?.standardWarrantyEndDate, filtersState?.standardWarrantyEndDateMatchMode);
    pushFilter('extendedWarrantyEndDate', filtersState?.extendedWarrantyEndDate, filtersState?.extendedWarrantyEndDateMatchMode);
    pushFilter('modifiedName', filtersState?.modifiedName, filtersState?.modifiedNameMatchMode);
    pushFilter('modifiedDate', filtersState?.modifiedDate, filtersState?.modifiedDateMatchMode);
    pushFilter('waitingApprovalInfo', filtersState?.waitingApprovalInfo, filtersState?.waitingApprovalInfoMatchMode);
    if (activeIndex === 6) {
        (filtersState?.statusId || []).forEach((val) => {
            filters.push({ column: backendColumnMap['statusId'], operator: 5, value: String(val) });
        });
    }

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = getBackendColumnName(field);
    if (!column) return null;
    return [
        {
            column,
            direction: order === 1 ? 1 : 2
        }
    ];
};
